import { api, type ApiType } from 'lib/api';
import { stringParse } from 'utils';

/**
 * Multipart `EntityType` value for legacy `POST /api/v2.0/fileupload`.
 * Mirrors the server-side enum; the server is the source of truth for allowed values.
 */
export const LEGACY_FILE_UPLOAD_ENTITY_TYPE_TEMP = 'temp';

/**
 * EntityType value for facility certificate PDF uploads.
 * Used to identify and validate certificate-specific uploads (20MB PDF limit).
 */
export const LEGACY_FILE_UPLOAD_ENTITY_TYPE_CERTIFICATE = 'certificate';

export const apiSuppliers: ApiType = {
  get: {
    search: ({ query, vars }) => api.get(stringParse('/api/suppliers?search={0}', vars), query),
    list: () => api.get('/api/suppliers?isActive=true'),
    listItemSuppliers: () => api.get('/api/suppliers?isActive=true&isItemSupplier=true'),
    listItemSuppliersSearch: ({ query, vars }) =>
      api.get(
        stringParse('/api/suppliers?isActive=true&isItemSupplier=true&search={0}', vars),
        query
      ),
    listStyleSuppliersSearch: ({ query, vars }) =>
      api.get(
        stringParse('/api/suppliers?isActive=true&isStyleSupplier=true&search={0}', vars),
        query
      ),
    listSupplierPortalEnabled: ({ vars }) =>
      api.get(
        stringParse(
          '/api/suppliers?isActive=true&supplierPortalEnabled=true&isStyleSupplier=true&search={0}',
          vars
        )
      ),
    listActiveWithPortal: ({ query }) =>
      api.get('/api/suppliers?supplierPortalEnabled=true', query),
    /**
     * Brand–Supplier Active Connections: all linked suppliers (legacy + portal orgs).
     * Omits supplierPortalEnabled so BE returns both Organisation types; ConnectionStatus
     * is derived from association + Organisation.SupplierPortalEnabled + merge records.
     */
    listActiveConnections: ({ query }) => api.get('/api/suppliers', query),
    /** Brand–Supplier Connection: pending invites / connection requests (DL-61067). */
    pendingConnections: ({ query }) => api.get('/api/suppliers/pending-connections', query),
    /** Brand–Supplier Connection: Active / Pending tab badge counts. */
    connectionCounts: () => api.get('/api/suppliers/counts'),
    legacyOrgAdmins: ({ vars }) => api.get(stringParse('/api/suppliers/{0}/admins', vars)),
    /**
     * Invite Supplier modal search (dl-61824 on develop).
     * Live route is `/api/supplier-search` (not under `/api/suppliers/…`).
     */
    searchForInvite: ({ vars }) => api.get(stringParse('/api/supplier-search?search={0}', vars)),
    byDirectSupplierId: ({ vars }) => api.get(stringParse('/api/suppliers/{0}', vars)),
    facilityProcesstype: () => api.get('/api/facilityprocesstype'),
    /** Process category reference data for PO creation (Step 4: map processes). */
    processCategories: () => api.get('/api/process-categories'),
    facilitiesList: ({ vars, query }) =>
      api.get(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities', vars), query),
    /** Supplier portal: all facilities for the logged-in supplier org (DL-60437). */
    orgFacilitiesList: ({ vars, query }) =>
      api.get(stringParse('/api/organizations/{0}/facilities', vars), query),
    /**
     * Full certificate rows for direct-supplier Certificates tab (flat shape per table columns).
     * `GET /api/organizations/{orgId}/suppliers/{supplierId}/certificates`
     */
    directSupplierCertificatesList: ({ vars, query }) =>
      api.get(stringParse('/api/organizations/{0}/suppliers/{1}/certificates', vars), query),
    /** Supplier portal: all certificates for the logged-in supplier org. */
    allCertificatesList: ({ vars, query }) =>
      api.get(stringParse('/api/organizations/{0}/facility-certificates', vars), query),
    /** Org-wide facilities for certificate facility picker (`organizationId` query param). */
    organizationFacilitiesList: ({ vars, query }) =>
      api.get('/api/facilities', { organizationId: vars?.[0], pageSize: '500', ...query }),
    globalSubSupplierList: ({ vars }) =>
      api.get(stringParse('/api/meta/search/global-sub-suppliers?search={0}', vars)),
    associatedBrands: ({ query }) => api.get('/api/supplierportalaccount/associated-brands', query),
    /** Supplier Brands page — Connection requests tab (dl-61828/dl-62020). Flat, unpaginated. */
    connectionRequests: () => api.get('/api/supplierportalaccount/connection-requests'),
    mergeRequest: ({ vars }) => api.get(stringParse('/api/suppliers/{0}/mergerequests/{1}', vars)),
    /**
     * Legacy→portal merge preview (dl-61770): resolve a supplier-provided merge/invite
     * code to the portal org it belongs to, before submitting the merge request.
     */
    supplierOrganizationByInviteCode: ({ vars }) =>
      api.get(stringParse('/api/supplierorganizations/{0}', vars)),
    facilityCertificatesList: ({ vars }) =>
      api.get(
        stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates', vars)
      ),
    orgFacilityCertificatesList: ({ vars }) =>
      api.get(
        stringParse(
          '/api/organizations/{0}/facility-certificates?facilityIds={2}&pageSize=200',
          vars
        )
      ),
  },
  post: {
    create: ({ vars, data }) =>
      api.post(stringParse('/api/organizations/{0}/organizationsubsuppliers', vars), data),
    createFacilities: ({ vars, data }) => {
      return api.post(
        stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities', vars),
        data
      );
    },
    /**
     * Org-scoped facility create for a direct supplier (the logged-in supplier org itself).
     * Sub-supplier is omitted, so the facility is owned directly by the org. Vars: `[orgId]`.
     */
    orgFacilitiesCreate: ({ vars, data }) =>
      api.post(stringParse('/api/organizations/{0}/facilities', vars), data),
    /**
     * Multipart temp upload for certificate files.
     * Legacy-compatible endpoint returns a file resource payload (`id` used as `documentResourceId`).
     */
    facilityCertificateDocumentUpload: ({ data }) =>
      api.post('/api/v2.0/fileupload', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
      }),
    /** JSON: `CreateFacilityCertificateRequest` — certificate metadata (no file required). */
    facilityCertificateCreate: ({ vars, data }) =>
      api.post(
        stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates', vars),
        data
      ),
    /**
     * Org-scoped create for directly-owned facilities (`facilityId` in body).
     * `POST /api/organizations/{orgId}/facility-certificates`
     */
    organizationFacilityCertificateCreate: ({ vars, data }) =>
      api.post(stringParse('/api/organizations/{0}/facility-certificates', vars), data),
    signup: ({ data }) => api.post('/api/supplierportalaccount', data),
    sendInviteCode: ({ data }) => api.post('/api/supplierportalaccount/invite-code/send', data),
    mergeRequestAction: ({ vars, data }) =>
      api.post(stringParse('/api/suppliers/{0}/mergerequests/{1}', vars), data),
    /**
     * Legacy→portal merge request (dl-61770): initiate the merge on the brand side once
     * the invite code has been previewed. `POST /api/suppliers/{legacySupplierId}/merge-requests`.
     */
    createSupplierMergeRequest: ({ vars, data }) =>
      api.post(stringParse('/api/suppliers/{0}/merge-requests', vars), data),
    /**
     * Net-new invite (dl-61766 / live BE dl-61833).
     * Body: `{ inviteEmail }` — brand org taken from auth context.
     */
    createNetNewConnection: ({ data }) => api.post('/api/supplier-connections', data),
    /**
     * Connect-existing (dl-62112 / Flow 4) — same live route as net-new.
     * Body: `{ supplierOrgId, message? }`.
     */
    createExistingConnection: ({ data }) => api.post('/api/supplier-connections', data),
    /** Resend a pending invite / connection request. Vars: `[pendingId]`. */
    resendPendingConnection: ({ vars }) =>
      api.post(stringParse('/api/suppliers/pending-connections/{0}/resend', vars)),
    legacyRegisterInvites: ({ vars, data }) =>
      api.post(stringParse('/api/suppliers/{0}/legacy-register-invites', vars), data),
  },
  put: {
    updateSupplier: ({ vars, data }) => api.put(stringParse('/api/suppliers/{0}', vars), data),
    /** JSON partial update — `UpdateFacilityCertificateRequest` (certificate fields + optional document). */
    facilityCertificateUpdate: ({ vars, data }) =>
      api.put(
        stringParse(
          '/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates/{3}',
          vars
        ),
        data
      ),
    /** Org-scoped update — `PUT /api/organizations/{orgId}/facility-certificates/{certId}` */
    organizationFacilityCertificateUpdate: ({ vars, data }) =>
      api.put(stringParse('/api/organizations/{0}/facility-certificates/{1}', vars), data),
    byId: ({ data, vars }) =>
      api.put(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facility/{2}', vars), data),
    /**
     * Org-scoped facility update for a direct supplier (works for direct and sub-supplier-owned).
     * Vars: `[orgId, facilityId]`.
     */
    orgFacilityById: ({ data, vars }) =>
      api.put(stringParse('/api/organizations/{0}/facilities/{1}', vars), data),
    bulkUpdate: ({ data, vars }) =>
      api.put(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facility', vars), data),
    /**
     * Org-scoped bulk archive/restore (works for direct and sub-supplier-owned facilities).
     * Body: array of `{ id, status }`. Vars: `[orgId]`. DL-60407.
     */
    orgBulkUpdateFacilities: ({ data, vars }) =>
      api.put(stringParse('/api/organizations/{0}/facilities', vars), data),
    bulkDeleteFacilities: ({ vars, data }) =>
      api.put(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities', vars), data),
    facilityCertificate: ({ data, vars }) =>
      api.put(
        stringParse(
          '/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates/{3}',
          vars
        ),
        data
      ),
  },
  delete: {
    /** Cancel a pending invite / connection request. Vars: `[pendingId]`. */
    cancelPendingConnection: ({ vars }) =>
      api.delete(stringParse('/api/suppliers/pending-connections/{0}', vars)),
    byFacilityId: ({ vars }) =>
      api.delete(stringParse('/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}', vars)),
    /** Org-scoped single delete (direct or sub-supplier-owned). Vars: `[orgId, facilityId]`. DL-60407. */
    orgByFacilityId: ({ vars }) =>
      api.delete(stringParse('/api/organizations/{0}/facilities/{1}', vars)),
    /**
     * Org-scoped bulk delete — HTTP DELETE with a body (array of facility ids).
     * apisauce forwards `axiosConfig.data` as the request body. Vars: `[orgId]`. DL-60407.
     */
    orgBulkDeleteFacilities: ({ vars, data }) =>
      api.delete(stringParse('/api/organizations/{0}/facilities', vars), {}, { data }),
    facilityCertificate: ({ vars }) =>
      api.delete(
        stringParse(
          '/api/organizations/{0}/sub-suppliers/{1}/facilities/{2}/certificates/{3}',
          vars
        )
      ),
    /** Org-scoped delete — `DELETE /api/organizations/{orgId}/facility-certificates/{certId}` */
    organizationFacilityCertificate: ({ vars }) =>
      api.delete(stringParse('/api/organizations/{0}/facility-certificates/{1}', vars)),
    /** Pending certificate file upload (before certificate metadata create). Removes server-side pending document. */
    facilityCertificateDocument: ({ vars }) =>
      api.delete(
        stringParse('/api/organizations/{0}/facilities/{1}/certificate-document/{2}', vars)
      ),
  },
};
